import { useQuery } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import type { ComponentDiagnostic } from "@swot/base-components";
import type { PermissionContext } from "@swot/swot-components";
import type { CmtsHostRuntime } from "../../../app/appRuntime.js";
import { getStoredTenantId } from "../auth/authStore.js";
import { resolveCurrentCmtsSession } from "../session/cmtsSessionResolver.js";
import type { RepositoryResult } from "../repositories/repositoryResult.js";
import type { CmtsQuery } from "../view-models/complaintViewModels.js";
import { throwCmtsCanonicalError } from "../api/cmtsApiErrors.js";
import { optionalCmtsGuid, requireCmtsGuid } from "../utils/cmtsGuid.js";

export type CmtsConnectedContainerProps = {
  runtime: CmtsHostRuntime;
  permissionContext?: PermissionContext | null | undefined;
};

export type ResultEnvelope<T> = RepositoryResult<T> | undefined;

export function tenantId(runtime: CmtsHostRuntime): string | undefined {
  const resolved = resolveCurrentCmtsSession();
  return runtime.config.tenantId ?? (resolved.status === "ready" ? resolved.session.tenantId : undefined) ?? getStoredTenantId();
}

export function requireTenantId(runtime: CmtsHostRuntime, operation: string): string {
  const resolvedTenantId = tenantId(runtime);
  if (!resolvedTenantId) {
    throwCmtsCanonicalError({
      code: "CMTS_ERR_MISSING_TENANT_CONTEXT",
      message: `${operation} requires an authenticated tenant context.`,
      details: { operation },
    });
  }
  return requireCmtsGuid(resolvedTenantId, `${operation} tenantId`);
}

export function requireLinkedComplaintId(row: Record<string, unknown>, operation: string): string {
  return requireCmtsGuid(row.complaintId ?? row.linkedEntityId, `${operation} complaintId`);
}

export function requireBackendRowId(row: Record<string, unknown>, keys: readonly string[], operation: string): string {
  for (const key of keys) {
    const candidate = row[key];
    const guid = optionalCmtsGuid(candidate);
    if (guid) return guid;
  }
  throwCmtsCanonicalError({
    code: "CMTS_ERR_INVALID_ROUTE_PARAM",
    message: `${operation} requires a persisted backend GUID id.`,
    details: { operation, keys },
  });
}

export function optionOf(row: Record<string, unknown>, fallbackPrefix: string): { value: string; label: string } {
  const value = String(row.id ?? row.value ?? row.code ?? row.userId ?? row.departmentId ?? "").trim();
  const label = String((row.label ?? row.name ?? row.displayName ?? row.value ?? row.code ?? value) || fallbackPrefix).trim();
  return { value: value || label, label: label || value || fallbackPrefix };
}

export function businessValueOptionOf(row: Record<string, unknown>, fallbackPrefix: string): { value: string; label: string } {
  const label = String((row.label ?? row.name ?? row.displayName ?? row.value ?? row.code ?? row.id) || fallbackPrefix).trim();
  const value = String((row.value ?? row.label ?? row.name ?? row.displayName ?? row.code ?? row.id) || label).trim();
  return { value: value || label, label: label || value || fallbackPrefix };
}


export function masterCrudDisplayName(payload: Record<string, unknown>, ...fallbacks: readonly unknown[]): unknown {
  return payload.displayName ?? payload.label ?? payload.name ?? payload.code ?? fallbacks.find((item) => item !== undefined && item !== null && String(item).trim() !== "");
}

export function masterCrudPayload(
  masterType: string,
  payload: Record<string, unknown>,
  tenant: string | undefined,
  displayName: unknown
): Record<string, unknown> {
    const versionNum =
    typeof payload.version === "number"
      ? payload.version
      : typeof payload.version === "string"
        ? parseInt(payload.version, 10) || 1
        : 1;
  const metadata: Record<string, unknown> = {
    ...(typeof payload.metadata === "object" && payload.metadata !== null
      ? (payload.metadata as Record<string, unknown>)
      : {}),
    ...(payload.entityType !== undefined ? { entityType: payload.entityType } : {}),
    ...(payload.sourceType !== undefined ? { sourceType: payload.sourceType } : {}),
    ...(payload.visibility !== undefined ? { visibility: payload.visibility } : {}),
    ...(payload.requiredFields !== undefined ? { requiredFields: payload.requiredFields } : {}),
    ...(payload.schemaDefinition !== undefined ? { schemaDefinition: payload.schemaDefinition } : {}),
    ...(payload.validationRules !== undefined ? { validationRules: payload.validationRules } : {}),
    ...(payload.category !== undefined ? { category: payload.category } : {}),
    ...(payload.schemaName !== undefined ? { schemaName: payload.schemaName } : {}),
    ...(payload.schema !== undefined ? { schema: payload.schema } : {}),
  };

  const body: Record<string, unknown> = {
    ...payload,
    group: payload.group ?? masterType,
    masterType: payload.masterType ?? masterType,
    name: displayName,
    displayName: displayName,
    version: versionNum,
    metadata,
  };

  if (tenant !== undefined) body.tenantId = tenant;

  return body;
}

export function createOrUpdateMasterData(runtime: CmtsHostRuntime, masterType: string, payload: Record<string, unknown>, tenant: string | undefined, displayName: unknown): ReturnType<CmtsHostRuntime["repositories"]["masterData"]["create"]> {
  const backendId = optionalCmtsGuid(payload.id);
  const body = masterCrudPayload(masterType, payload, tenant, displayName);
  if (!backendId) {
    delete body.id;
    return runtime.repositories.masterData.create(masterType, body, tenant);
  }
  return runtime.repositories.masterData.update(masterType, backendId, { ...body, id: backendId }, tenant);
}

export function deactivateMasterData(runtime: CmtsHostRuntime, masterType: string, row: Record<string, unknown>, reason: string, tenant: string | undefined): ReturnType<CmtsHostRuntime["repositories"]["masterData"]["deactivate"]> {
  const backendId = requireCmtsGuid(row.id, "Master data deactivate");
  return runtime.repositories.masterData.deactivate(masterType, backendId, reason, tenant);
}

export function reactivateMasterData(runtime: CmtsHostRuntime, masterType: string, row: Record<string, unknown>, reason: string, tenant: string | undefined): ReturnType<CmtsHostRuntime["repositories"]["masterData"]["activate"]> {
  const backendId = requireCmtsGuid(row.id, "Master data reactivate");
  return runtime.repositories.masterData.activate(masterType, backendId, reason, tenant);
}

export async function loadMasterOptions(runtime: CmtsHostRuntime, masterType: string, tenant: string | undefined, fallbackPrefix: string): Promise<readonly { value: string; label: string }[]> {
  try {
    const result = await runtime.repositories.masterData.dropdown(masterType, tenant);
    if (!result?.ok || !Array.isArray(result.data)) return [];
    return result.data.filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null && (row as Record<string, unknown>).active !== false && (row as Record<string, unknown>).status !== "Inactive").map((row) => businessValueOptionOf(row, fallbackPrefix));
  } catch {
    return [];
  }
}

export function activeRows(rows: readonly Record<string, unknown>[]): readonly Record<string, unknown>[] {
  return rows.filter((row) => row.active !== false && row.isActive !== false && String(row.status ?? "Active").toLowerCase() !== "inactive");
}

export function dataOr<T>(result: ResultEnvelope<T>, fallback: T): T {
  if (!result) return fallback;
  if (result.ok) return result.data;
  return fallback;
}

const EMPTY_ADMIN_DEPENDENCY_DATA: Record<string, readonly Record<string, unknown>[]> = {
  complaints: [],
  tasks: [],
  workflowHistory: [],
  notifications: [],
  calendarEvents: [],
  reportDefinitions: [],
  reportExports: [],
  routingRules: [],
  fieldSchemas: [],
  masterData: [],
};

export function useAdminDependencyData(runtime: CmtsHostRuntime): Record<string, readonly Record<string, unknown>[]> {
  const tenant = tenantId(runtime);
  const query = useQuery({
    queryKey: ["cmts", "admin-dependency-data", tenant],
    retry: 1,
    staleTime: 60_000, 
    queryFn: async () => {
      const rowsFrom = <T,>(result: RepositoryResult<T> | undefined): readonly Record<string, unknown>[] => {
        if (!result?.ok || !Array.isArray(result.data)) return [];
        return result.data.filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null && !Array.isArray(row));
      };
      const tenantScoped = (rows: readonly Record<string, unknown>[] = []) => rows.filter((row) => String(row.tenantId ?? tenant) === String(tenant ?? row.tenantId ?? ""));
      const masterTypes = ["workflow-status", "workflow-stage", "priority", "severity", "department", "site", "product", "part", "process", "line", "machine", "report-type", "dashboard-kpi"] as const;
      const [complaints, tasks, notifications, reportDefinitions, reportExports, routingRules, fieldSchemas, ...masterResults] = await Promise.all([
        runtime.repositories.complaints.list({ tenantId: tenant, pageNumber: 1, pageSize: 100 }),
        runtime.repositories.tasks.my(tenant),
        runtime.repositories.notifications.my(tenant),
        runtime.repositories.reports.listCatalog(tenant),
        runtime.repositories.reports.listExports(tenant),
        runtime.repositories.reference.routing(tenant),
        runtime.repositories.reference.fieldSchemas(tenant),
        ...masterTypes.map((masterType) => runtime.repositories.masterData.dropdown(masterType, tenant)),
      ]);
      const masterData = masterResults.flatMap((result) => rowsFrom(result));
      return {
        complaints: tenantScoped(rowsFrom(complaints)),
        tasks: tenantScoped(rowsFrom(tasks)),
        workflowHistory: [],
        notifications: tenantScoped(rowsFrom(notifications)),
        calendarEvents: [],
        reportDefinitions: tenantScoped(rowsFrom(reportDefinitions)),
        reportExports: tenantScoped(rowsFrom(reportExports)),
        routingRules: tenantScoped(rowsFrom(routingRules)),
        fieldSchemas: tenantScoped(rowsFrom(fieldSchemas)),
        masterData: tenantScoped(masterData),
      };
    },
  });
  return query.data ?? EMPTY_ADMIN_DEPENDENCY_DATA;
}

export function invalidateAdminArea(queryClient: QueryClient, tenant: string | undefined, keys: readonly unknown[]) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: ["cmts", "master-data"] }),
    queryClient.invalidateQueries({ queryKey: ["cmts", "reference-admin", tenant] }),
    queryClient.invalidateQueries({ queryKey: ["cmts", "organization-tree", tenant] }),
    queryClient.invalidateQueries({ queryKey: ["cmts", "routing", tenant] }),
    queryClient.invalidateQueries({ queryKey: ["cmts", "field-schemas", tenant] }),
    queryClient.invalidateQueries({ queryKey: ["cmts", "dashboard"] }),
    ...keys.map((key) => queryClient.invalidateQueries({ queryKey: Array.isArray(key) ? key : [key] })),
  ]);
}

export function diagnosticsOf(...sources: Array<ResultEnvelope<unknown> | readonly ComponentDiagnostic[] | null | undefined>): readonly ComponentDiagnostic[] {
  const diagnostics: ComponentDiagnostic[] = [];
  for (const source of sources) {
    if (!source) continue;
    if (Array.isArray(source)) {
      diagnostics.push(...(source as readonly ComponentDiagnostic[]));
    } else if ("diagnostics" in source) {
      diagnostics.push(...source.diagnostics);
    }
  }
  return diagnostics;
}

export function errorOf(result: ResultEnvelope<unknown>): string | null {
  return result && !result.ok ? result.diagnostics.map((d) => d.message).join("; ") || "CMTS repository returned an error." : null;
}

export function assertRepositoryOk<T>(result: RepositoryResult<T>, actionName: string): T {
  if (result.ok) return result.data;
  const message = result.diagnostics.map((diagnostic) => diagnostic.message).filter(Boolean).join("; ") || `${actionName} failed.`;
  throwCmtsCanonicalError({ code: "CMTS_ERR_ADAPTER_FAILURE", message, details: { actionName, diagnostics: result.diagnostics.map((diagnostic) => ({ code: diagnostic.code, message: diagnostic.message })) } });
}

export function emptyBackendDashboard(type: string, tenantIdValue: string | undefined) {
  return {
    dashboardType: type,
    tenantId: tenantIdValue ?? "",
    generatedAtUtc: new Date().toISOString(),
    generatedAtLocal: new Date().toLocaleString(),
    timezoneCode: "Asia/Kolkata",
    stale: false,
    restricted: false,
    diagnostics: [{ code: "CMTS_DASHBOARD_BACKEND_EMPTY", message: "The backend dashboard endpoint returned no dashboard payload/cards.", severity: "warning" as const }],
    cards: [],
  };
}

export function complaintQuery(runtime: CmtsHostRuntime): CmtsQuery {
  return { tenantId: tenantId(runtime), pageNumber: 1, pageSize: 50, sortBy: "updatedAt", sortDirection: "DESC" };
}

export function useCmtsRouterSearch(): string {
  const [searchParams] = useSearchParams();
  const serialized = searchParams.toString();
  return serialized ? `?${serialized}` : "";
}


